import { useMutation } from "@tanstack/react-query";
import ReactECharts from "echarts-for-react";
import { useState } from "react";
import { api } from "../api/client.js";
import { ErrorBanner, InfoTooltip, Kpi, Panel, pct } from "../components/Common.js";

interface FcnResponse {
  runId: string;
  modelVersion: string;
  seed: number;
  pathCount: number;
  marketDataWarnings: { ticker: string; code: string; message: string }[];
  result: {
    fairAnnualizedCouponPct: number;
    couponPerObservationPct: number;
    expectedLifeYears: number;
    autocallProbabilityByObservation: number[];
    cumulativeAutocallProbability: number;
    probabilityReachesMaturity: number;
    probabilityKnockIn: number;
    expectedPrincipalRedemptionPct: number;
    standardError: number;
    correlationUsed: number[][];
    correlationWasRepaired: boolean;
  };
}

export function FcnPage() {
  const [tickersInput, setTickersInput] = useState("AAPL,MSFT,NVDA");
  const [tenor, setTenor] = useState("1Y");
  const [observationFrequency, setObservationFrequency] = useState("quarterly");
  const [koLevelPct, setKoLevelPct] = useState(1.0);
  const [kiLevelPct, setKiLevelPct] = useState(0.7);
  const [strikeLevelPct, setStrikeLevelPct] = useState(1.0);
  const [correlationOverride, setCorrelationOverride] = useState<string>("");

  const mutation = useMutation({
    mutationFn: () =>
      api.post<FcnResponse>("/pricing/fcn", {
        tickers: tickersInput.split(",").map((t) => t.trim().toUpperCase()).filter(Boolean),
        valuationDate: new Date().toISOString().slice(0, 10),
        tenor,
        observationFrequency,
        koLevelPct,
        kiLevelPct,
        strikeLevelPct,
        notional: 100,
        correlationOverride: correlationOverride ? Number(correlationOverride) : undefined,
        pricingQuality: "standard",
      }),
  });

  return (
    <div className="space-y-3">
      <Panel title="Contractual inputs">
        <div className="grid grid-cols-2 gap-3 md:grid-cols-6">
          <div className="md:col-span-2">
            <label className="field-label">Tickers (worst-of basket, comma-separated)</label>
            <input className="field-input" value={tickersInput} onChange={(e) => setTickersInput(e.target.value)} />
          </div>
          <div>
            <label className="field-label">Tenor</label>
            <select className="field-input" value={tenor} onChange={(e) => setTenor(e.target.value)}>
              {["3M", "6M", "1Y", "18M", "2Y", "3Y", "5Y"].map((t) => (
                <option key={t} value={t}>
                  {t}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="field-label">Observation freq.</label>
            <select className="field-input" value={observationFrequency} onChange={(e) => setObservationFrequency(e.target.value)}>
              <option value="monthly">Monthly</option>
              <option value="quarterly">Quarterly</option>
              <option value="semiannual">Semiannual</option>
              <option value="annual">Annual</option>
            </select>
          </div>
          <div>
            <label className="field-label">KO level (%)</label>
            <input type="number" step="0.01" className="field-input" value={koLevelPct} onChange={(e) => setKoLevelPct(Number(e.target.value))} />
          </div>
          <div>
            <label className="field-label">
              KI level (%)<InfoTooltip text="Maturity-only (European) knock-in by default — see docs/payoffs.md." />
            </label>
            <input type="number" step="0.01" className="field-input" value={kiLevelPct} onChange={(e) => setKiLevelPct(Number(e.target.value))} />
          </div>
        </div>
        <div className="mt-2 grid grid-cols-2 gap-3 md:grid-cols-6">
          <div>
            <label className="field-label">Strike level (%)</label>
            <input type="number" step="0.01" className="field-input" value={strikeLevelPct} onChange={(e) => setStrikeLevelPct(Number(e.target.value))} />
          </div>
          <div>
            <label className="field-label">
              Correlation override<InfoTooltip text="Overrides the estimated historical correlation with a scalar — never hidden from the diagnostics panel." />
            </label>
            <input
              className="field-input"
              placeholder="e.g. 0.5 (2-asset only)"
              value={correlationOverride}
              onChange={(e) => setCorrelationOverride(e.target.value)}
            />
          </div>
        </div>
        <button className="btn-primary mt-3" onClick={() => mutation.mutate()} disabled={mutation.isPending}>
          {mutation.isPending ? "Solving for fair coupon…" : "Solve fair coupon"}
        </button>
      </Panel>

      {mutation.error && <ErrorBanner message={(mutation.error as Error).message} />}

      {mutation.data && (
        <>
          {mutation.data.result.correlationWasRepaired && (
            <div className="rounded-md border border-amber-800/50 bg-amber-950/30 px-3 py-2 text-xs text-amber-300">
              The correlation matrix used for pricing required PSD repair before use.
            </div>
          )}
          <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
            <Kpi label="Fair annualized coupon" value={pct(mutation.data.result.fairAnnualizedCouponPct)} />
            <Kpi label="Coupon per observation" value={pct(mutation.data.result.couponPerObservationPct)} />
            <Kpi label="Cumulative autocall probability" value={pct(mutation.data.result.cumulativeAutocallProbability)} />
            <Kpi label="P(reaches maturity)" value={pct(mutation.data.result.probabilityReachesMaturity)} />
            <Kpi label="P(knock-in / downside)" value={pct(mutation.data.result.probabilityKnockIn)} />
            <Kpi label="Expected principal redemption" value={pct(mutation.data.result.expectedPrincipalRedemptionPct)} />
            <Kpi label="Expected life" value={`${mutation.data.result.expectedLifeYears.toFixed(2)}y`} />
            <Kpi label="Std. error" value={mutation.data.result.standardError.toFixed(4)} />
          </div>

          <Panel title="Autocall probability by observation">
            <ReactECharts
              option={{
                grid: { left: 40, right: 20, top: 20, bottom: 30 },
                xAxis: {
                  type: "category",
                  data: mutation.data.result.autocallProbabilityByObservation.map((_, i) => `Obs ${i + 1}`),
                  axisLabel: { color: "#94a3b8" },
                },
                yAxis: { type: "value", axisLabel: { color: "#94a3b8", formatter: "{value}" } },
                tooltip: {},
                series: [
                  {
                    type: "bar",
                    data: mutation.data.result.autocallProbabilityByObservation.map((v) => Number((v * 100).toFixed(2))),
                    itemStyle: { color: "#3b82f6" },
                  },
                ],
              }}
              style={{ height: 280 }}
            />
          </Panel>
        </>
      )}
    </div>
  );
}
